import { act, useEffect, useState } from "react";
import { api } from "../../redux/api";
import { Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import LightTooltip from "../../commonComponents/LightTooltip";
import { addAdditionalTemplateHeaderDetails, rcTreeToOutboundTemplate } from "../../utils/rctreeDataConversion";
import { treeActions } from "../../redux/slices/treeSlice";
import { isEqual } from "lodash";
import AiAutoMapButton from "./AiAutoMapButton";
import AiAutoMapConfirmDialog from "./AiAutoMapConfirmDialog";
import axios from "axios";
import { eachDeep } from "deepdash-es/standalone";
import { findAIMappedFields, getKeysToExpandForMappedNodes } from "../../utils/autoMapping";


export default function AiAutoMapping() {
  //hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();

  //redux state variables
  const obTreeState = useSelector(state => state.treeData.outbound.rightTree);
  const leftTreeSelectedNode = useSelector(state => state.treeData.outbound.leftTree.tree.body.selectedNode);
  const templateHeader = obTreeState?.tree?.templateHeader;
  const { baseUrl, account, user, token} = useSelector(state => state.queryParams);
  const { rightTree } = useSelector(state => state.treeData.outbound);
  const currentTab = rightTree.currentTab;
  const { translatedData } = rightTree.tree[currentTab];
  const triggerAiAutoMap = useSelector(state => state.treeData.triggerAiAutoMap);
  const mappingStates = useSelector(state => state.treeData.mappingStates);
  //states
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [mappingInProgress, setMappingInProgress] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [aiAutoMapButtonClicked, setAiAutoMapButtonClicked] = useState(false);
  const [triggeredFromCsvMenu, setTriggeredFromCsvMenu] = useState(false);
  const [aiMappingJustCompleted, setAiMappingJustCompleted] = useState(false);

  //temporary button enabled const. templateHeader has DEPENDENCIES property, enable button.
  const buttonEnabled = (templateHeader?.DEPENDENCIES) ? true : false; //hardcoded CSV for early development

  //send current outbound template data to backend as an unsaved template
  const sendUnsavedChanges = (callback) => {
    const wfg = obTreeState?.path?.split('/')?.[0] || null;
    const name = obTreeState?.path?.split('/')?.[1] || null;
    const convertedData = rcTreeToOutboundTemplate(obTreeState.tree);
    const templateHasDiff = !isEqual(obTreeState.initialFetchData, convertedData);

    const requiredDataForTemplateHeader = {
      ACCOUNT_NAME: account,
      TEMPLATE: name,
      WF_GROUP: wfg,
    }

    //handle save response
    const handleResponse = (response) => {
      if (response?.data?.current_state === "SUCCESS") {
        //save successful, return true
        return callback(true, 'diff-save-success');
      } else {
        //save failed, return false with info
        return callback(false, 'diff-save-fail');
      }
    }

    //dispatch save call
    const saveTemplate = () => {
      dispatch(api.endpoints.saveUserOutboundTemplate.initiate(
        {
          account,
          workflowGroupName: wfg,
          templateName: encodeURIComponent(name),
          data: addAdditionalTemplateHeaderDetails(convertedData, requiredDataForTemplateHeader,true)
        }, { forceRefetch: true }
      )).then((res) => {
        handleResponse(res);
      })
    }

    //route logic based on template diff
    if (templateHasDiff) {
      if (account && wfg && name) {
        //template has diff, send unsaved changes to backend
        saveTemplate();
      } else {
        //has diff but missing required data for saving the template, return false with info
        return callback(false, "diff-no-data");
      }
    } else {
      //no changes to save, return true
      return callback(true, 'no-diff');
    }
  }
  const updateSelectedNodeInRightTree = (latestData) => {
    const dataToSearch = latestData || translatedData;
    
    if (!dataToSearch) {
      return null;
    }
    
    if (!rightTree?.tree[rightTree.currentTab]?.selectedNode?.key) {
      return dataToSearch[0];
    }
  
    let matchingNode = null;
    const currentKey = rightTree.tree[currentTab].selectedNode.key;
  
    eachDeep(dataToSearch, (obj) => {
      if (obj?.key === currentKey) {
        matchingNode = obj;
        return false;
      }
      if (Array.isArray(obj?.children)) {
        return obj.children;
      }
    });
    
    return matchingNode;
  };

  //function to automatically select AI-mapped fields and show mapping lines
  // Select and show mapping lines for AI-mapped fields (AI_CONFIDENCE=true)
  const selectAIMappedFields = () => {
    // Only proceed if we're in mapping mode
    if (!mappingStates?.isMappingMode) {
      return;
    }

    try {
      // Helper to find all AI-mapped fields (only AI_CONFIDENCE, not AI_AUTOMAP)
      const findAITargetFields = (treeData) => {
        const result = [];
        eachDeep(treeData, (node) => {
          // Only select fields that actually came back from AI with confidence scores
          if (node && node.hasOwnProperty('AI_CONFIDENCE') && node.AI_CONFIDENCE) {
            result.push(node);
          }
          if (Array.isArray(node?.children)) return node.children;
        });
        return result;
      };

      // Get keys for all tabs
      const aiMappedKeys = { header: [], body: [], trailer: [] };
      let totalAIFields = 0;
      
      ['header', 'body', 'trailer'].forEach(tab => {
        const tabData = rightTree.tree[tab]?.translatedData;
        if (tabData) {
          const aiFields = findAITargetFields(tabData);
          aiMappedKeys[tab] = aiFields.map(field => field.key).filter(key => key);
          totalAIFields += aiFields.length;
        }
      });

      // Only proceed if there are actual AI-mapped fields
      if (totalAIFields === 0) {
        return;
      }

      // Select AI-mapped fields in each tab
      Object.entries(aiMappedKeys).forEach(([tabName, fieldKeys]) => {
        if (fieldKeys.length > 0) {
          const tabIndex = { header: 0, body: 1, trailer: 2 }[tabName];
          const tabData = rightTree.tree[tabName]?.translatedData;
          
          if (tabData) {
            const aiMappedNodes = findAITargetFields(tabData);
            
            // Expand parent nodes to make AI-mapped fields visible
            const keysToExpand = [];
            aiMappedNodes.forEach(node => {
              if (node.key) {
                // Find all parent keys by traversing up the tree
                const findParentKeys = (data, targetKey, parentKeys = []) => {
                  for (const item of data) {
                    if (item.key === targetKey) {
                      return parentKeys;
                    }
                    if (item.children && item.children.length > 0) {
                      const found = findParentKeys(item.children, targetKey, [...parentKeys, item.key]);
                      if (found) return found;
                    }
                  }
                  return null;
                };
                
                const parentKeys = findParentKeys(tabData, node.key);
                if (parentKeys) {
                  keysToExpand.push(...parentKeys);
                }
              }
            });
            
            // Remove duplicates and expand parents
            const uniqueKeysToExpand = [...new Set(keysToExpand)];
            if (uniqueKeysToExpand.length > 0) {
              dispatch(treeActions.requestTreeExpansion({
                tree: 'rightTree',
                tab: tabName,
                keysToExpand: uniqueKeysToExpand
              }));
            }
            
            console.log(aiMappedNodes, "AI Mapped Nodes");
            
            // Add delay to ensure tree expansion completes before setting selections
            setTimeout(() => {
              dispatch(treeActions.setSelectedKeysArray({
                screen: 'outbound',
                type: tabName,
                data: fieldKeys
              }));
              
              dispatch(treeActions.setMappingSelectedTargetNodes({
                tab: tabIndex,
                selectedNodes: aiMappedNodes
              }));
              
              // Also trigger a custom event to update the local tree state
              window.dispatchEvent(new CustomEvent('updateTreeSelection', {
                detail: {
                  tab: tabName,
                  selectedKeys: fieldKeys
                }
              }));
              
              // Trigger force expansion recheck to update mapping lines
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('forceExpansionRecheck'));
              }, 100);
              
            }, 300); // Delay to allow tree expansion to complete
          }
        }
      });
    
    } catch (error) {
      console.warn('Error in selectAIMappedFields:', error);
    }
  };

  //user confirmed dialog, proceed to auto map template.
  //send an initial automap request, then poll at 1000ms to check status.
  //after status returns current_state SUCCESS, fetch updated template data, and fetch unsaved templates.
  const handleAutoMapTemplate = () => {
    //toggle aiAutoMapping flag true before engpoint calls
    dispatch(treeActions.setAiAutoMapping(true));

    const wfgIn = templateHeader?.DEPENDENCIES?.split('/')?.[0];
    const inbound = templateHeader?.DEPENDENCIES?.split('/')?.[2];
    const wfgOut = leftTreeSelectedNode?.workflowGroup;
    const outbound = leftTreeSelectedNode?.title;

    const fetchUpdatedTemplate = () => {
      setConfirmationModalOpen(false);

      dispatch(api.endpoints.getOutboundTemplate.initiate(
        {
          account,
          workflowGroupName: wfgOut,
          templateName: encodeURIComponent(outbound)
        }, { forceRefetch: true }
      )).then((res) => {
        dispatch(treeActions.setRightTreeLoading({
          screen: 'outbound',
          data: false
        }));
        dispatch(treeActions.setAiAutoMapping(false));
        dispatch(api.endpoints.getUnsavedTemplates.initiate(
          { account }, { forceRefetch: true }
        ));
        
        // After template is fetched, automatically select AI-mapped fields
        setTimeout(() => {
          setAiMappingJustCompleted(true);
          selectAIMappedFields();
        }, 500); // Small delay to ensure template data is fully loaded
        
      }).catch((err) => {
        console.warn('getOutboundTemplate fetch error:', err);
        dispatch(treeActions.setRightTreeLoading({
          screen: 'outbound',
          data: false
        }));
        dispatch(treeActions.setAiAutoMapping(false));
        dispatch(api.endpoints.getUnsavedTemplates.initiate(
          { account }, { forceRefetch: true }
        ));
      });

    }

    const startAutoMapPolling = () => {

      dispatch(treeActions.setRightTreeLoading({
        screen: 'outbound',
        data: true
      }));

      const pollRequest = () => {
        axios.get(`${baseUrl}/workbench`, {
          params: {
            action: 'get_ai_historical_mappings_status',
            user: user,
            account: account,
            WfGroupName_in: wfgIn,
            inbound: inbound,
            WfGroupName_out: wfgOut,
            outbound: outbound
          },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }).then((res) => {
          //display the status provided by polling res.
          if (res?.data?.current_state === "PROCESSING") {
            if (statusMessage !== res?.data?.message) {
              setStatusMessage(res?.data?.message);
            }
          }
          //if success, stop loading states
          if (res?.data?.current_state === "SUCCESS" || res?.data?.current_state === "ERROR") {
            clearInterval(pollLoop);
            fetchUpdatedTemplate();
          }
        }).catch((err) => {
          console.warn(err);
          clearInterval(pollLoop);
          dispatch(treeActions.setAiAutoMapping(false));
          dispatch(treeActions.setRightTreeLoading({
            screen: 'outbound',
            data: false
          }));
          setConfirmationModalOpen(false);
        });
      }

      const pollLoop = setInterval(() => {
        pollRequest();
      }, 1000);

    }

    if (baseUrl && user && account && wfgIn && inbound && wfgOut && outbound) {
      dispatch(treeActions.setRightTreeLoading({
        screen: 'outbound',
        data: true
      }));

      dispatch(api.endpoints.getAiAutoMapping.initiate(
        {
          user,
          account,
          WfGroupName_in: wfgIn,
          inbound,
          WfGroupName_out: wfgOut,
          outbound,
          ...(triggeredFromCsvMenu && { map_only_selected: true })
        }, 
        {
          forceRefetch: true
        }
      )).then(() => {
        startAutoMapPolling();
        // Reset the CSV menu flag after API call
        setTriggeredFromCsvMenu(false);
      }).catch((err) => {
        console.warn(err);
        dispatch(treeActions.setAiAutoMapping(false));
        setConfirmationModalOpen(false);
        dispatch(treeActions.setRightTreeLoading({
          screen: 'outbound',
          data: false
        }));
      });
    } else {
      //missing required data for AI auto-map, handle this case.
      //TODO: make some error dialog for this case.
      console.warn("Missing required data for AI auto-map call");
    }
  }
  
  //handle button click by sending unsaved changes, if any, to the backend, then call the AI auto-map function.
  const handleAiButtonClick = () => {
    setAiAutoMapButtonClicked(true);
    //start by checking for template diff, use callback function to handle success/failure cases.
    sendUnsavedChanges((success, info) => {
      if (success) {
        //template saved successfully, call the AI auto-map function.
        setStatusMessage('');
        return setConfirmationModalOpen(true);
      } else {
        setAiAutoMapButtonClicked(false);
        //handle failure to save template. 
        //TODO: make some error dialog for this case.
        return;
      }
    });
  };

  const handleDialogCancel = () => {
    setConfirmationModalOpen(false);
  }

  const handleDialogConfirm = () => {
    handleAutoMapTemplate();
  }
  
  useEffect(() => {
    if (translatedData && aiAutoMapButtonClicked) {
      const matchingNode = updateSelectedNodeInRightTree(translatedData);
      if (matchingNode) {
        dispatch(treeActions.setRightTreeSelectedNode({
          screen: "outbound",
          type: currentTab,
          data: matchingNode,
        }));
        setAiAutoMapButtonClicked(false);
      }
    }
  }, [translatedData, currentTab]);

  // Listen for triggerAiAutoMap flag from context menu
  useEffect(() => {
    if (triggerAiAutoMap?.triggered) {
      // Store whether it was triggered from CSV menu
      setTriggeredFromCsvMenu(triggerAiAutoMap.fromCsvMenu);
      // Reset the trigger flag
      dispatch(treeActions.setTriggerAiAutoMap({ triggered: false, fromCsvMenu: false }));
      // Call the AI button click handler
      handleAiButtonClick();
    }
  }, [triggerAiAutoMap?.triggered]);

  // Watch for when AI auto mapping completes and new translated data arrives
  useEffect(() => {
    // Only trigger if:
    // 1. We're in mapping mode
    // 2. We're not in loading state
    // 3. We have translated data
    // 4. AI mapping just completed
    // 5. We're not currently processing mapping
    if (mappingStates?.isMappingMode && 
        !obTreeState?.loading && 
        translatedData && 
        !mappingInProgress &&
        aiMappingJustCompleted) {
      
      try {
        // Check if there are any AI-mapped fields in the current translated data
        const aiMappedFields = [];
        eachDeep(translatedData, (node) => {
          if (node && node.hasOwnProperty('AI_CONFIDENCE') && node.AI_CONFIDENCE) {
            aiMappedFields.push(node);
          }
          if (Array.isArray(node?.children)) return node.children;
        });
        
        // If we have AI-mapped fields, select them
        if (aiMappedFields.length > 0) {
          selectAIMappedFields();
        }
      } catch (error) {
        console.warn('Error selecting AI-mapped fields:', error);
      } finally {
        // Reset the flag regardless of success or error
        setAiMappingJustCompleted(false);
      }
    }
  }, [translatedData, obTreeState?.loading, mappingInProgress, aiMappingJustCompleted, mappingStates?.isMappingMode]);

  return (
    <>
      <AiAutoMapButton 
        buttonEnabled={buttonEnabled} 
        handleAiButtonClick={handleAiButtonClick}
        hasInboundDependency={!!templateHeader?.DEPENDENCIES}
      />
      <AiAutoMapConfirmDialog 
        modalOpen={confirmationModalOpen} 
        handleDialogCancel={handleDialogCancel}
        handleDialogConfirm={handleDialogConfirm}
        mappingInProgress={mappingInProgress}
        statusMessage={statusMessage}
      />
    </>
  );
}
